// DS: CR58
/** An image link in card form that displays art and title
 *  associated with a topic.
 */
import React, { useContext } from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  BiContextProvidedValue,
  BiData,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import DeviationDumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';

import s from './TopicCard.scss';

export const HOVERED_IMAGE_COUNT = 4;

export interface Props {
  /** resource type for topics */
  typeId?: number;
  /** neco id for this topic */
  itemId?: number;
  /** the topic location in the app (e.g. '/topic/digital_art') */
  url?: string;
  /** the topic name (e.g. 'Digital Art') */
  name: string;
  /** deviations used for a cover and an internal grid when hovered over */
  deviations?: PapiDeviation[];
  width: number;
  className?: string;
  rank?: number;
}

const TopicCard: React.FC<Props> = props => {
  const isMobile = useContext(MobileContext);
  const { url, name, deviations, width, className, typeId, itemId, rank } =
    props;
  if (!url || !name || !width) {
    return null;
  }

  let images: PapiDeviation[] = [],
    coverDeviation;
  if (deviations?.length) {
    images = deviations.slice(1, HOVERED_IMAGE_COUNT);
    coverDeviation = deviations[0];
  }
  const classNames = classnames(
    s.root,
    className,
    !coverDeviation && s['missing-bg']
  );

  const linkStyles = { width, height: width };
  // floor was causing a single pixel gap on odd width, browser wanted a .5
  const halfWidth = Number((width / 2).toFixed(2));
  return (
    <BiLink
      href={url}
      style={linkStyles}
      className={classNames}
      biData={BiData<ItemClickBiEvent>({
        evid: 100,
        sectionname: BiContextProvidedValue,
        typeid: typeId,
        itemid: itemId,
        rank,
      })}
    >
      {/* a cover image that shows when not hovered */}
      {coverDeviation && (
        <DeviationDumbo
          className={classnames(s[`cover`], s['deviation'])}
          deviation={coverDeviation}
          key={coverDeviation.title}
          width={width}
          height={width}
        />
      )}

      {/* images that appear when hovered on desktop */}
      {!isMobile &&
        images &&
        images.map((deviation, i) => (
          <DeviationDumbo
            key={deviation.deviationId}
            className={classnames(s[`on-hover-${i}`], s['deviation'])}
            deviation={deviation}
            width={halfWidth}
            height={halfWidth}
          />
        ))}

      <div className={s['overlay']} />
      <h2 className={s['name']}>{name}</h2>
    </BiLink>
  );
};
TopicCard.displayName = 'TopicCard';
export default TopicCard;
